import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import {
  type ProductDetailsContent,
  type ProductDetailsMappedContent,
} from '../../ProductDetails.types.ts';

// ─────────────────────────────────────────────────────────────────────────────

export const useProductDetailsContent = (params: Params) => {
  const { baseProductSlug } = params;

  // ─── Remote Data ─────────────────────────────────────────────────────

  const searchParams = useMemo(
    () => ({ 'fields.baseProductSlug': baseProductSlug }),
    [baseProductSlug],
  );

  const { data, fetching } =
    useContentfulContentTypeEntry<ProductDetailsContent>({
      contentType: PRODUCT_DETAILS_CONTENT_TYPE,
      searchParams,
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const superIngredients = useMemo(() => {
    const entries = data?.fields?.superIngredients ?? [];

    return entries.map((ingredient) => ingredient.fields);
  }, [data?.fields?.superIngredients]);

  const mappedContent = useMemo<ProductDetailsMappedContent>(() => {
    return {
      subheading: data?.fields?.subheading,
      description: data?.fields?.description,
      superIngredients,
    };
  }, [data?.fields?.description, data?.fields?.subheading, superIngredients]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    fetching,
    mappedContent,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PRODUCT_DETAILS_CONTENT_TYPE = 'productDetails';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  baseProductSlug: string;
};
